import {ref, type Ref} from 'vue';
import type {ComponentPublicInstance} from '@vue/runtime-core';

/** Handle returned by useSectionRefs — see the composable's JSDoc for the contract. */
export interface SectionRefsHandle {
  /** Reactive array of refs by index, populated by the template `:ref` callback. */
  refs: Ref<ComponentPublicInstance[]>;
  /**
   * Vue template-ref callback. Wire on a `v-for`'d component:
   * `:ref="el => sectionRefs.assign(el, i)"`.
   *
   * Grow-only by design: a null callback (Vue fires it on unmount) is ignored,
   * not used to clear the slot. The full array is reset between page changes
   * via `reset()`, so stale entries never persist across navigations — and
   * within a page the list only grows as new section-components mount.
   */
  assign(el: unknown, index: number): void;
  /** Resolve the just-revealed element for `useScrollToElement`'s target resolver. */
  resolve(index: number): HTMLElement | null;
  /** Clear all refs. Call from the `pid` watcher before the new page renders. */
  reset(): void;
}

/**
 * Per-index template refs for a v-for of `<section-component>`. Used by
 * `session-cms-content.vue` and `homework-modal.vue` to track the rendered
 * cards and look up the just-revealed one for smooth scroll targeting.
 */
export function useSectionRefs(): SectionRefsHandle {
  const refs = ref<ComponentPublicInstance[]>([]);
  return {
    refs,
    assign(el: unknown, index: number): void {
      if (el) {
        refs.value[index] = el as ComponentPublicInstance;
      }
    },
    resolve(index: number): HTMLElement | null {
      return (refs.value[index]?.$el as HTMLElement | undefined) ?? null;
    },
    reset(): void {
      refs.value = [];
    },
  };
}
